import React, { useCallback, useMemo } from "react";
import InputField from "../../shared-components/InputField";
import AddButton from "../../shared-components/AddButton";
import { Jobs } from "../../api/Person";
import { XIcon } from "../../shared-components/icons";
import { uniqueUUID } from "../../utils";
import { classNames } from "../../utils";
import { toDateInputValue, isValidDateRange } from "./helpers";
import { useRowForm } from "./useRowForm";
import { PersonEditSectionModal } from "./PersonEditSectionModal";

export interface JobFormRow {
  title: string;
  employer: string;
  country: string;
  start_date: string;
  end_date: string;
  key: string;
  external_id?: string | null;
}

function toFormRow(job: Jobs, key: string): JobFormRow {
  return {
    title: job.title ?? "",
    employer: job.employer ?? "",
    country: job.country ?? "",
    start_date: toDateInputValue(job.start_date) || "",
    end_date: toDateInputValue(job.end_date) || "",
    key,
    external_id: job.external_id ?? undefined,
  };
}

function toJobPayload(row: JobFormRow) {
  return {
    title: row.title.trim(),
    employer: row.employer.trim(),
    country: row.country.trim(),
    start_date: row.start_date.trim() || "",
    end_date: row.end_date.trim() || "",
    external_id: row.external_id ?? null,
  };
}

interface EditEmploymentModalProps {
  open: boolean;
  onClose: () => void;
  initialJobs: Jobs[];
  onSave: (jobs: Array<ReturnType<typeof toJobPayload>>) => Promise<void>;
  onDirtyChange: (dirty: boolean) => void;
  isSaving?: boolean;
}

const emptyJobRow = (): JobFormRow => ({
  title: "",
  employer: "",
  country: "",
  start_date: "",
  end_date: "",
  key: uniqueUUID(),
});

const EditEmploymentModal: React.FC<EditEmploymentModalProps> = ({
  open,
  onClose,
  initialJobs,
  onSave,
  onDirtyChange,
  isSaving = false,
}) => {
  const initialFormRows = useMemo(
    () =>
      initialJobs.length > 0
        ? initialJobs.map((j) => toFormRow(j, j.external_id ?? uniqueUUID()))
        : [],
    [initialJobs]
  );
  const getEmptyRow = useCallback(emptyJobRow, []);

  const { rows, updateRow, addRow, removeRow } = useRowForm(
    open,
    initialFormRows,
    getEmptyRow,
    onDirtyChange
  );

  const validRows = rows.every((r) =>
    isValidDateRange(r.start_date, r.end_date)
  );
  const hasValidJob = rows.some(
    (r) =>
      (r.title.trim() || r.employer.trim()) &&
      isValidDateRange(r.start_date, r.end_date)
  );
  const submitDisabled =
    !validRows || (!hasValidJob && rows.some((r) => r.title || r.employer));

  const handleSave = useCallback(async () => {
    const payload = rows
      .map(toJobPayload)
      .filter((j) => j.title || j.employer)
      .filter((j) => isValidDateRange(j.start_date, j.end_date));
    await onSave(payload.length > 0 ? payload : []);
  }, [rows, onSave]);

  return (
    <PersonEditSectionModal
      open={open}
      onClose={onClose}
      onSave={handleSave}
      isSaving={isSaving}
      submitDisabled={submitDisabled}
      saveErrorMessage="Failed to save employment. Please try again."
    >
      <div className="pt-2">
        <div className="gap-2 p-4 border border-gray-200 rounded-lg bg-gray-100 space-y-4">
          <h3 className="text-sm font-bold text-gray-800">
            Employment History
          </h3>
          <div className="space-y-4">
            {rows.map((row, idx) => (
              <div key={row.key} className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <InputField
                    label="Job Title"
                    value={row.title}
                    handleChange={(e) =>
                      updateRow(idx, "title", e.target.value)
                    }
                    name={`job-title-${idx}`}
                    placeholder="e.g. Software Engineer"
                    classes="min-w-0 w-full"
                  />
                  <InputField
                    label="Company Name"
                    value={row.employer}
                    handleChange={(e) =>
                      updateRow(idx, "employer", e.target.value)
                    }
                    name={`job-employer-${idx}`}
                    placeholder="Company name"
                    classes="min-w-0 w-full"
                  />
                  <InputField
                    label="Start Date"
                    type="date"
                    value={row.start_date}
                    handleChange={(e) =>
                      updateRow(idx, "start_date", e.target.value)
                    }
                    name={`job-start-${idx}`}
                    classes="min-w-0 w-full"
                  />
                </div>
                <div
                  className={classNames(
                    "grid gap-3",
                    rows.length > 1 ? "grid-cols-[1fr_1fr_auto]" : "grid-cols-2"
                  )}
                >
                  <InputField
                    label="End Date"
                    type="date"
                    value={row.end_date}
                    handleChange={(e) =>
                      updateRow(idx, "end_date", e.target.value)
                    }
                    name={`job-end-${idx}`}
                    classes="min-w-0 w-full"
                  />
                  <InputField
                    label="Country"
                    value={row.country}
                    handleChange={(e) =>
                      updateRow(idx, "country", e.target.value)
                    }
                    name={`job-country-${idx}`}
                    placeholder="e.g. US"
                    classes="min-w-0 w-full"
                  />
                  {rows.length > 1 && (
                    <div className="flex items-end pb-1">
                      <button
                        type="button"
                        className="w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700 border border-gray-300"
                        onClick={() => removeRow(idx)}
                        aria-label="Remove job"
                      >
                        <XIcon />
                      </button>
                    </div>
                  )}
                </div>
                {!isValidDateRange(row.start_date, row.end_date) &&
                  (row.start_date || row.end_date) && (
                    <p className="text-sm text-red-600">
                      Start date must be on or before end date.
                    </p>
                  )}
              </div>
            ))}
          </div>
          <AddButton
            text="Add Job"
            onClick={addRow}
            className="text-blue-600 hover:opacity-80 font-medium"
          />
        </div>
      </div>
    </PersonEditSectionModal>
  );
};

export default EditEmploymentModal;
